import React, { useMemo } from "react";
import functions from "../../util/functions";
import { Link } from "react-router-dom";
import { useTheme } from "../../DataStore/ThemeContext";


const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const TransactionResult = ({ transaction }) => {
  const {
    ledger_no,
    account,
    debit,
    credit,
    description,
    transaction_date,
    created_by,
    tag,
    ledger_grouping,
    payee,
    created_at,
  } = transaction || {};

  const { debitAccount, creditAccount, debitAmount, creditAmount } = useMemo(() => {
    // If this record has credit, it’s likely the credit leg; use grouping for the debit leg.
    const isThisCreditLeg = toNum(credit) > 0;

    const debitAcc = isThisCreditLeg ? ledger_grouping?.account : account;
    const creditAcc = isThisCreditLeg ? account : ledger_grouping?.account;

    const crAmt =
      toNum(credit) > 0 ? toNum(credit) : toNum(ledger_grouping?.credit) > 0 ? toNum(ledger_grouping?.credit) : 0;

    const drAmt =
      toNum(debit) > 0 ? toNum(debit) : toNum(ledger_grouping?.debit) > 0 ? toNum(ledger_grouping?.debit) : 0;

    return {
      debitAccount: debitAcc,
      creditAccount: creditAcc,
      debitAmount: drAmt,
      creditAmount: crAmt,
    };
  }, [account, debit, credit, ledger_grouping]);

  const {closeSettings} = useTheme();

  return (
    <>
      {/* component-only CSS (you can move into global css) */}
      <style>{`
        .yx-trx{
          font-family: var(--yx-font, system-ui);
          color: var(--yx-text, #1e293b);
        }

        /* header */
        .yx-trxHead{
          padding: 18px 18px 14px;
          border-bottom: 1px solid rgba(15,23,42,.08);
          background: linear-gradient(180deg, rgba(79,70,229,.06), rgba(255,255,255,0));
        }

        .yx-trxKicker{
          display:flex; align-items:center; gap:10px;
          font-size: 12px;
          font-weight: 900;
          text-transform: uppercase;
          color: rgba(100,116,139,.9);
        }

        

        .yx-trxJe{
          margin-top: 10px;
          font-weight: 950;
          letter-spacing: -0.03em;
          line-height: 1.05;
          font-size: 25px;
          color: var(--yx-accent, #4F46E5);
        }

        .yx-trxDesc{
          margin-top: 10px;
          font-size: 14px;
          font-weight: 750;
          color: rgba(100,116,139,.92);
          line-height: 1.45;
        }

        /* body */
        .yx-trxBody{
          padding-top: 18px;
        }

        .yx-trxGrid{
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }

        /* “account cards” */
        .yx-acCard{
          border-radius: 16px;
          border: 1px solid rgba(15,23,42,.08);
          background: rgba(255,255,255,.9);
          box-shadow: 0 1px 2px rgba(16,24,40,.03);
          padding: 14px 14px 12px;
          min-width: 0;
        }

        .yx-acTitle{
          display:flex; align-items:center; justify-content:space-between;
          gap: 12px;
          margin-bottom: 10px;
        }

        .yx-acLabel{
          font-weight: 900;
          font-size: 12px;
          text-transform: uppercase;
          color: rgba(100,116,139,.9);
        }

        .yx-acBadge{
          display:inline-flex;
          align-items:center;
          gap: 8px;
          height: 28px;
          padding: 0 10px;
          border-radius: 999px;
          border: 1px solid rgba(79,70,229,.16);
          background: rgba(79,70,229,.08);
          color: var(--yx-accent, #4F46E5);
          font-weight: 900;
          font-size: 12px;
          white-space: nowrap;
        }

        .yx-acName{
          font-weight: 950;
          font-size: 16px;
          color: rgba(30,41,59,.95);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .yx-acMeta{
          margin-top: 6px;
          display:flex;
          gap: 12px;
          flex-wrap: wrap;
          color: rgba(100,116,139,.92);
          font-weight: 800;
          font-size: 13px;
        }

        .yx-acMeta span{
          display:inline-flex; align-items:center; gap: 8px;
          padding: 6px 10px;
          border-radius: 12px;
          background: rgba(15,23,42,.03);
          border: 1px solid rgba(15,23,42,.07);
          white-space: nowrap;
        }

        .yx-acAmt{
          margin-top: 10px;
          font-weight: 950;
          font-size: 20px;
          color: rgba(15,23,42,.92);
        }

        .yx-acAmt small{
          font-weight: 900;
          font-size: 13px;
          color: rgba(100,116,139,.92);
          margin-right: 6px;
        }

        /* details list */
        .yx-metaList{
          margin-top: 14px;
          border-radius: 16px;
          border: 1px solid rgba(15,23,42,.08);
          overflow: hidden;
          background: #fff;
        }

        .yx-metaRow{
          display:flex;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 14px;
          border-top: 1px solid rgba(15,23,42,.06);
        }
        .yx-metaRow:first-child{ border-top: 0; }

        .yx-metaKey{
          font-weight: 900;
          color: rgba(100,116,139,.92);
          font-size: 13px;
          display:flex;
          align-items:center;
          gap: 10px;
          white-space: nowrap;
        }
        .yx-metaKey i{
          width: 28px; height: 28px;
          border-radius: 12px;
          display:flex; align-items:center; justify-content:center;
          background: rgba(79,70,229,.08);
          border: 1px solid rgba(79,70,229,.12);
          color: var(--yx-accent, #4F46E5);
          flex: 0 0 auto;
        }

        .yx-metaVal{
          font-weight: 950;
          font-size: 13px;
          color: rgba(30,41,59,.95);
          text-align: right;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        @media (max-width: 768px){
          .yx-trxJe{ font-size: 34px; }
          .yx-trxGrid{ grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="yx-trx">
        {/* Header */}
        <div className="yx-trxHead">
        <div className="d-flex justify-content-between align-items-start">

          <div>
            <div className="yx-trxKicker">Transaction</div>
            <div className="yx-trxJe">
              JE: {ledger_no || "—"}
            </div>
          </div>

          <Link
            to={`/transaction-ledger/${ledger_no}`}
            className="yx-viewLedgerBtn"
            onClick={closeSettings}
          >
            <i className="fa fa-external-link-alt mr-2" />
            View Ledger Details
          </Link>

        </div>

      {description && (
        <div className="yx-trxDesc">
          {description}
        </div>
      )}
    </div>

        {/* Body */}
        <div className="yx-trxBody">
          <div className="yx-trxGrid">
            {/* Debit */}
            <div className="yx-acCard">
              <div className="yx-acTitle">
                <div className="yx-acLabel">Debit account</div>
                <span className="yx-acBadge">
                  <i className="fa fa-arrow-down" />
                  Dr
                </span>
              </div>

              <div className="yx-acName" title={debitAccount?.account_name}>
                {debitAccount?.account_name || "N/A"}
              </div>

              <div className="yx-acMeta">
                <span title="Account Number">
                  <i className="fa fa-hashtag" style={{ color: "rgba(100,116,139,.85)" }} />
                  {debitAccount?.account_number || "N/A"}
                </span>
              </div>

              <div className="yx-acAmt">
                <small>Dr:</small> {functions.numberFormat(debitAmount)}
              </div>
            </div>

            {/* Credit */}
            <div className="yx-acCard">
              <div className="yx-acTitle">
                <div className="yx-acLabel">Credit account</div>
                <span className="yx-acBadge">
                  <i className="fa fa-arrow-up" />
                  Cr
                </span>
              </div>

              <div className="yx-acName" title={creditAccount?.account_name}>
                {creditAccount?.account_name || "N/A"}
              </div>

              <div className="yx-acMeta">
                <span title="Account Number">
                  <i className="fa fa-hashtag" style={{ color: "rgba(100,116,139,.85)" }} />
                  {creditAccount?.account_number || "N/A"}
                </span>
              </div>

              <div className="yx-acAmt">
                <small>Cr:</small> {functions.numberFormat(creditAmount)}
              </div>
            </div>
          </div>

          {/* Meta rows */}
          <div className="yx-metaList">
            <div className="yx-metaRow">
              <div className="yx-metaKey">
                <i className="fa fa-calendar-alt" />
                Transaction date
              </div>
              <div className="yx-metaVal" title={String(transaction_date || "")}>
                {transaction_date ? functions.formatDate(transaction_date) : "—"}
              </div>
            </div>

            <div className="yx-metaRow">
              <div className="yx-metaKey">
                <i className="fa fa-user" />
                Created by
              </div>
              <div className="yx-metaVal" title={created_by?.full_name || created_by?.username || ""}>
                {created_by?.full_name || created_by?.username || "N/A"}
              </div>
            </div>

            <div className="yx-metaRow">
              <div className="yx-metaKey">
                <i className="fa fa-tag" />
                Tag
              </div>
              <div className="yx-metaVal" title={tag?.tag_name || ""}>
                {tag?.tag_name || "N/A"}
              </div>
            </div>

            <div className="yx-metaRow">
              <div className="yx-metaKey">
                <i className="fa fa-clock" />
                Recorded on
              </div>
              <div className="yx-metaVal" title={String(created_at || "")}>
                {created_at ? functions.formatDate(created_at) : "—"}
              </div>
            </div>

            <div className="yx-metaRow">
              <div className="yx-metaKey">
                <i className="fa fa-handshake" />
                Payee
              </div>
              <div className="yx-metaVal" title={payee?.title || ""}>
                {payee?.title || "N/A"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransactionResult;
